import React, { useContext } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRepeat } from '@fortawesome/free-solid-svg-icons';
import GameAssetsContext from '../../../../../../context/GameAssetContext';
import { GameLog } from '../../../../Logs/LogsDisplay';
import { getUniversalSourceIcon } from './StatChangeEvent';
import './AggregatedStackEvent.css';

interface AggregatedStackEventProps {
  log: GameLog;
  isGreyscale?: boolean;
}

const AggregatedStackEvent: React.FC<AggregatedStackEventProps> = ({ log, isGreyscale = false }) => {
  const { gameWideAssets, championsAssets } = useContext(GameAssetsContext);

  const details = log.details;
  // Use 'source' field (which is what the backend sends) or fallback to 'stackName'
  const stackName = details.source || details.stackName || '';
  const { stackType, currentStacks, maxStacks, championName, tickCount } = details;
  const imageState = log.imageState;

  // Use the universal image fetching function
  const sourceIcon = getUniversalSourceIcon(
    {
      sourceType: stackType === 'Champion Passive' ? 'Passive' : stackType,
      imageState: imageState,
      sourceName: stackName,
      championName: championName,
      abilityKey: stackType === 'Ability' ? stackName : undefined, // Pass stackName as abilityKey for abilities
    },
    gameWideAssets,
    championsAssets
  );

  // Apply greyscale class if needed
  let finalSourceIcon = sourceIcon;
  if (isGreyscale && React.isValidElement(sourceIcon) && sourceIcon.type === 'img') {
    finalSourceIcon = React.cloneElement(sourceIcon as React.ReactElement<any>, {
      className: `${sourceIcon.props.className} greyscale`
    });
  }

  // Determine stack display: show "current/max" if maxStacks exists and is meaningful, otherwise just show current
  const stackDisplay = (maxStacks && maxStacks !== 9999) 
    ? `${currentStacks}/${maxStacks}` 
    : `${currentStacks}`;

  // Create tooltip text
  const tooltipText = tickCount && tickCount > 1 
    ? `${stackName} reached max stacks (${maxStacks}/${maxStacks}) and refreshed duration ${tickCount} times`
    : `${stackName} stacks: ${stackDisplay}`;

  return (
    <div 
      className={`aggregated-stack-event ${isGreyscale ? 'greyscale' : ''}`}
      title={tooltipText}
    >
      {finalSourceIcon}
      <div className="stack-text-container">
        <span className="stack-count">
          {stackDisplay}
        </span>
        {tickCount && tickCount > 1 && (
          <div className="stack-repeat-badge">
            <FontAwesomeIcon icon={faRepeat} className="stack-repeat-icon" />
            <span className="stack-repeat-count">{tickCount}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default AggregatedStackEvent; 